import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  CircularProgress,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ApproveIcon,
} from '@mui/icons-material';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { donationsApi, Donation } from '../services/donationsService.ts';

const CHARITY_TYPES = ['Nonprofit', 'Religious', 'Educational', 'Community', 'Other'];
const PAYMENT_METHODS = ['Cash', 'Check', 'Company Check', 'Card', 'ACH', 'Other'];
const STATUSES = ['Pending', 'Approved', 'Paid', 'Refunded'];

const emptyForm = {
  donationDate: '',
  charityName: '',
  charityType: '',
  einTaxId: '',
  contactPerson: '',
  donationPurpose: '',
  donationAmount: '',
  paymentMethod: '',
  checkTransactionNumber: '',
  companyBusinessUnit: '',
  approvedBy: '',
  receiptReceived: false,
  receiptNumber: '',
  taxDeductible: true,
  notes: '',
  status: 'Pending',
};

const headerCellSx = {
  color: '#fff',
  fontWeight: 700,
  fontSize: '0.8rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  border: '2px solid #7f0000',
  py: 1.5,
  px: 1.5,
  whiteSpace: 'nowrap' as const,
};

const statusColor = (status: string): 'default' | 'warning' | 'success' | 'error' | 'info' => {
  switch (status) {
    case 'Paid': return 'success';
    case 'Approved': return 'info';
    case 'Refunded': return 'error';
    default: return 'warning';
  }
};

const DonationsPage: React.FC = () => {
  const { showSuccess, showError } = useSnackbar();
  const { sessionToken, user } = useAuth();
  const isAdmin = user?.type === 'admin';
  const [entries, setEntries] = useState<Donation[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<Donation | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSaveError, setAutoSaveError] = useState('');
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    };
  }, []);

  const fetchEntries = async () => {
    if (!sessionToken) return;
    setLoading(true);
    try {
      const res = await donationsApi.getAll({ page: 1, limit: 500 }, sessionToken);
      setEntries(res.data);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to load entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [sessionToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const openAdd = () => {
    setEditingEntry(null);
    setForm({ ...emptyForm, donationDate: new Date().toISOString().slice(0, 10) });
    setDialogOpen(true);
  };

  const openEdit = (entry: Donation) => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
    setAutoSaveStatus('idle');
    setAutoSaveError('');
    setEditingEntry(entry);
    setForm({
      donationDate: entry.donationDate,
      charityName: entry.charityName,
      charityType: entry.charityType,
      einTaxId: entry.einTaxId || '',
      contactPerson: entry.contactPerson || '',
      donationPurpose: entry.donationPurpose,
      donationAmount: entry.donationAmount.toString(),
      paymentMethod: entry.paymentMethod,
      checkTransactionNumber: entry.checkTransactionNumber || '',
      companyBusinessUnit: entry.companyBusinessUnit || '',
      approvedBy: entry.approvedBy || '',
      receiptReceived: entry.receiptReceived,
      receiptNumber: entry.receiptNumber || '',
      taxDeductible: entry.taxDeductible,
      notes: entry.notes || '',
      status: entry.status,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!sessionToken || !window.confirm('Delete this donation?')) return;
    try {
      await donationsApi.remove(id, sessionToken);
      showSuccess('Donation deleted.');
      fetchEntries();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete donation');
    }
  };

  const handleApprove = async (id: number) => {
    if (!sessionToken) return;
    try {
      await donationsApi.update(id, { status: 'Approved' }, sessionToken);
      showSuccess('Donation approved.');
      fetchEntries();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to approve donation');
    }
  };

  const validateForm = (f: typeof emptyForm): string | null => {
    if (
      !f.donationDate ||
      !f.charityName.trim() ||
      !f.charityType.trim() ||
      !f.donationPurpose.trim() ||
      !f.donationAmount ||
      !f.paymentMethod.trim()
    ) {
      return 'Please fill in all required fields.';
    }
    if (isNaN(parseFloat(f.donationAmount))) {
      return 'Donation Amount must be a valid number.';
    }
    return null;
  };

  const buildPayload = (f: typeof emptyForm) => ({
    donationDate: f.donationDate,
    charityName: f.charityName.trim(),
    charityType: f.charityType.trim(),
    einTaxId: f.einTaxId.trim() || undefined,
    contactPerson: f.contactPerson.trim() || undefined,
    donationPurpose: f.donationPurpose.trim(),
    donationAmount: parseFloat(f.donationAmount),
    paymentMethod: f.paymentMethod.trim(),
    checkTransactionNumber: f.checkTransactionNumber.trim() || undefined,
    companyBusinessUnit: f.companyBusinessUnit.trim() || undefined,
    approvedBy: f.approvedBy.trim() || undefined,
    receiptReceived: f.receiptReceived,
    receiptNumber: f.receiptNumber.trim() || undefined,
    taxDeductible: f.taxDeductible,
    notes: f.notes.trim() || undefined,
    status: f.status,
  });

  // Auto-saves ~900ms after the last change to any field, while editing an existing
  // donation. Adding a new donation still requires the explicit Add button.
  const performAutoSave = useCallback(
    async (id: number, snapshot: typeof emptyForm) => {
      if (!sessionToken) return;
      const validationError = validateForm(snapshot);
      if (validationError) {
        setAutoSaveStatus('error');
        setAutoSaveError(validationError);
        return;
      }
      setAutoSaveStatus('saving');
      try {
        await donationsApi.update(id, buildPayload(snapshot), sessionToken);
        setAutoSaveStatus('saved');
        fetchEntries();
      } catch (err) {
        setAutoSaveStatus('error');
        setAutoSaveError(err instanceof Error ? err.message : 'Failed to save changes');
      }
    },
    [sessionToken]
  );

  const handleFieldChange = <K extends keyof typeof emptyForm>(field: K, value: typeof emptyForm[K]) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (editingEntry) {
        if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
        setAutoSaveStatus('pending');
        autoSaveTimeoutRef.current = setTimeout(() => {
          autoSaveTimeoutRef.current = null;
          performAutoSave(editingEntry.id, next);
        }, 900);
      }
      return next;
    });
  };

  const handleCloseDialog = async () => {
    if (autoSaveTimeoutRef.current && editingEntry) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
      await performAutoSave(editingEntry.id, form);
    }
    setDialogOpen(false);
  };

  const handleSave = async () => {
    if (!sessionToken) return;
    const validationError = validateForm(form);
    if (validationError) {
      showError(validationError);
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload(form);
      if (editingEntry) {
        await donationsApi.update(editingEntry.id, payload, sessionToken);
        showSuccess('Donation updated.');
      } else {
        await donationsApi.create(payload, sessionToken);
        showSuccess('Donation added.');
      }
      setDialogOpen(false);
      fetchEntries();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save donation');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d: string) =>
    d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  const totalAmount = entries.reduce((s, e) => s + Number(e.donationAmount), 0);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ color: "#7f0000", fontWeight: 700, flex: "0 0 auto", whiteSpace: "nowrap" }}>
          Date: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          Donation / Charity
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAdd}
          sx={{ backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}
        >
          Add Donation
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {entries.length} donation{entries.length !== 1 ? 's' : ''}
        {entries.length > 0 && ` · Total Donated: $${totalAmount.toFixed(2)}`}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'auto', border: '2px solid #9e9e9e' }}>
          <TableContainer>
            <Table sx={{ borderCollapse: 'collapse', '& td': { border: '2px solid #9e9e9e', py: 1.25, px: 1.5 } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                  <TableCell sx={headerCellSx}>Donation ID</TableCell>
                  <TableCell sx={headerCellSx}>Date</TableCell>
                  <TableCell sx={headerCellSx}>Charity / Organization</TableCell>
                  <TableCell sx={headerCellSx}>Type</TableCell>
                  <TableCell sx={headerCellSx}>Purpose</TableCell>
                  <TableCell align="right" sx={headerCellSx}>Amount</TableCell>
                  <TableCell sx={headerCellSx}>Payment Method</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                  <TableCell align="center" sx={headerCellSx}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 5, border: '2px solid #9e9e9e', color: 'text.secondary' }}>
                      No donations yet. Click "Add Donation" to get started.
                    </TableCell>
                  </TableRow>
                )}
                {entries.map((entry, idx) => (
                  <TableRow
                    key={entry.id}
                    sx={{
                      backgroundColor: idx % 2 === 0 ? '#fff' : '#fdf5f5',
                      '&:hover': { backgroundColor: '#fce4e4' },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{entry.donationId}</TableCell>
                    <TableCell>{formatDate(entry.donationDate)}</TableCell>
                    <TableCell>{entry.charityName}</TableCell>
                    <TableCell>{entry.charityType}</TableCell>
                    <TableCell>{entry.donationPurpose}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="600">${Number(entry.donationAmount).toFixed(2)}</Typography>
                    </TableCell>
                    <TableCell>{entry.paymentMethod}</TableCell>
                    <TableCell>
                      <Chip label={entry.status} size="small" color={statusColor(entry.status)} />
                    </TableCell>
                    <TableCell align="center">
                      {isAdmin && (
                        <Tooltip title={entry.status === 'Approved' ? 'Already approved' : 'Approve'}>
                          <span>
                            <IconButton
                              size="small"
                              color="success"
                              disabled={entry.status === 'Approved'}
                              onClick={() => handleApprove(entry.id)}
                            >
                              <ApproveIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => openEdit(entry)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isAdmin ? (
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(entry.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Only admins can delete donations">
                          <span>
                            <IconButton size="small" disabled>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {entries.length > 0 && (
                  <TableRow sx={{
                    backgroundColor: '#b71c1c',
                    '& td': { border: '2px solid #7f0000', py: 1.25, px: 1.5, color: '#fff', fontWeight: 700 },
                  }}>
                    <TableCell colSpan={5}>TOTAL</TableCell>
                    <TableCell align="right">${totalAmount.toFixed(2)}</TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingEntry ? 'Edit Donation' : 'Add Donation'}</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            {editingEntry && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Donation ID"
                  fullWidth
                  disabled
                  value={editingEntry.donationId}
                  helperText="Auto-generated"
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Donation Date"
                type="date"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                value={form.donationDate}
                onChange={e => handleFieldChange('donationDate', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}><Divider sx={{ my: 1 }}>Charity Details</Divider></Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Charity / Organization Name"
                fullWidth
                required
                value={form.charityName}
                onChange={e => handleFieldChange('charityName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Charity Type</InputLabel>
                <Select
                  value={form.charityType}
                  label="Charity Type"
                  onChange={e => handleFieldChange('charityType', e.target.value)}
                >
                  {CHARITY_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="EIN / Tax ID"
                fullWidth
                placeholder="12-3456789"
                value={form.einTaxId}
                onChange={e => handleFieldChange('einTaxId', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Contact Person"
                fullWidth
                value={form.contactPerson}
                onChange={e => handleFieldChange('contactPerson', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}><Divider sx={{ my: 1 }}>Donation Details</Divider></Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Donation Purpose"
                fullWidth
                required
                value={form.donationPurpose}
                onChange={e => handleFieldChange('donationPurpose', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Donation Amount"
                type="number"
                fullWidth
                required
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
                value={form.donationAmount}
                onChange={e => handleFieldChange('donationAmount', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={form.paymentMethod}
                  label="Payment Method"
                  onChange={e => handleFieldChange('paymentMethod', e.target.value)}
                >
                  {PAYMENT_METHODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Check / Transaction #"
                fullWidth
                placeholder="CHK-10542"
                value={form.checkTransactionNumber}
                onChange={e => handleFieldChange('checkTransactionNumber', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Company / Business Unit"
                fullWidth
                value={form.companyBusinessUnit}
                onChange={e => handleFieldChange('companyBusinessUnit', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Approved By"
                fullWidth
                value={form.approvedBy}
                onChange={e => handleFieldChange('approvedBy', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}><Divider sx={{ my: 1 }}>Receipt & Tax</Divider></Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Receipt Received</InputLabel>
                <Select
                  value={form.receiptReceived ? 'Yes' : 'No'}
                  label="Receipt Received"
                  onChange={e => handleFieldChange('receiptReceived', e.target.value === 'Yes')}
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Receipt / Acknowledgment #"
                fullWidth
                placeholder="REC-2026-001"
                value={form.receiptNumber}
                onChange={e => handleFieldChange('receiptNumber', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Tax Deductible</InputLabel>
                <Select
                  value={form.taxDeductible ? 'Yes' : 'No'}
                  label="Tax Deductible"
                  onChange={e => handleFieldChange('taxDeductible', e.target.value === 'Yes')}
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={form.status}
                  label="Status"
                  onChange={e => handleFieldChange('status', e.target.value)}
                >
                  {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                minRows={2}
                value={form.notes}
                onChange={e => handleFieldChange('notes', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={editingEntry ? { justifyContent: 'space-between' } : undefined}>
          {editingEntry ? (
            <>
              <Typography
                variant="body2"
                color={autoSaveStatus === 'error' ? 'error' : 'text.secondary'}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}
              >
                {autoSaveStatus === 'saving' && (<><CircularProgress size={14} />Saving…</>)}
                {autoSaveStatus === 'pending' && 'Unsaved changes…'}
                {autoSaveStatus === 'saved' && 'All changes saved'}
                {autoSaveStatus === 'error' && (autoSaveError || 'Failed to save changes')}
              </Typography>
              <Button onClick={handleCloseDialog} variant="contained" disabled={autoSaveStatus === 'saving'}
                sx={{ backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}>
                Close
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{ backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : 'Add'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DonationsPage;
